import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { usePreferences, useUpdatePreferences } from '../hooks/use-preferences';
import { preferencesSchema, type PreferencesFormData } from '@/lib/validations/settings.schema';
import { Globe, Moon, Sun, Monitor, DollarSign } from 'lucide-react';
import { useConfigStore } from '@/lib/stores/config.store';
import { useUIStore } from '@/lib/stores/ui.store';
import { CURRENCIES, CURRENCY_SYMBOLS } from '@/lib/utils/constants';

export function PreferencesContainer() {
  const { t, i18n } = useTranslation();
  const { data: preferences, isLoading } = usePreferences();
  const { mutate: updatePreferences, isPending } = useUpdatePreferences();
  const { currency, setCurrency } = useConfigStore();
  const { theme: currentTheme, setTheme } = useUIStore();

  const form = useForm<PreferencesFormData>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      language: 'fr',
      theme: 'system',
    },
  });

  useEffect(() => {
    if (preferences) {
      form.reset({
        language: preferences.language as 'fr' | 'en' | 'ar',
        theme: preferences.theme,
      });
    }
  }, [preferences, form]);

  const handleSubmit = (data: PreferencesFormData) => {
    updatePreferences(data, {
      onSuccess: () => {
        if (data.language && data.language !== i18n.language) {
          i18n.changeLanguage(data.language);
        }
        if (data.theme && data.theme !== currentTheme) {
          setTheme(data.theme);
        }
      },
    });
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    form.setValue('theme', theme);
    setTheme(theme); // Apply immediately
  };

  const languages = [
    { value: 'fr', label: 'Français', flag: '🇫🇷' },
    { value: 'en', label: 'English', flag: '🇬🇧' },
    { value: 'ar', label: 'العربية', flag: '🇸🇦' },
  ];

  const themes = [
    { value: 'light', label: t('settings.preferences.themes.light'), icon: Sun },
    { value: 'dark', label: t('settings.preferences.themes.dark'), icon: Moon },
    { value: 'system', label: t('settings.preferences.themes.system'), icon: Monitor },
  ];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.preferences.title')}</CardTitle>
        <CardDescription>{t('settings.preferences.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Language */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Globe className="size-4" />
              {t('settings.preferences.language')}
            </Label>
            <Select
              value={form.watch('language')}
              onValueChange={(value: 'fr' | 'en' | 'ar') => form.setValue('language', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('settings.preferences.selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Currency */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="size-4" />
              {t('settings.preferences.currency')}
            </Label>
            <Select
              value={currency}
              onValueChange={(value) => setCurrency(value as typeof currency)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('settings.preferences.selectCurrency')} />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((cur) => (
                  <SelectItem key={cur} value={cur}>
                    <span className="flex items-center gap-2">
                      <span>{CURRENCY_SYMBOLS[cur]}</span>
                      <span>{t(`settings.currencies.${cur}`)}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Theme */}
          <div className="space-y-2">
            <Label>{t('settings.preferences.theme')}</Label>
            <div className="grid grid-cols-3 gap-2">
              {themes.map((theme) => {
                const Icon = theme.icon;
                const isSelected = currentTheme === theme.value;
                return (
                  <Button
                    key={theme.value}
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    className="flex flex-col items-center gap-2 h-auto py-4"
                    onClick={() => handleThemeChange(theme.value as 'light' | 'dark' | 'system')}
                  >
                    <Icon className="size-5" />
                    <span className="text-xs">{theme.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>

          <Button type="submit" disabled={isPending}>
            {isPending ? t('settings.preferences.saving') : t('settings.preferences.save')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
